import { TextInput } from '../../../components/TextInput';
import type { SamplePlayer, SampleTeam } from '../../../types/sampleData';
import { newSampleKey } from '../../../utils/sampleDataTemplate';
import { KeyedSelect, NumberField, SampleFieldset, SampleRow } from './SampleFormControls';
import {
  fieldId,
  playerName,
  POSITION_CHOICES,
  removePlayer,
  replaceAt,
  type KeyedOption,
  type SampleSectionProps,
} from './templateEditing';

const teamOptions = (teams: SampleTeam[]): KeyedOption[] =>
  teams.map((team, index) => ({ key: team.key, label: `${index + 1}. ${team.name || 'Unnamed team'}` }));

const emptyPlayer = (): SamplePlayer => ({
  key: newSampleKey('player'),
  firstName: '',
  lastName: '',
  position: '',
  gradYear: null,
  phone: '',
  email: '',
  teamKey: null,
});

const PlayersNotice = () => (
  <p className="sample-form__notice" data-testid="sample-data-players-notice">
    Messages and calls to these players really send. Use phone numbers and inboxes Seven Hills
    controls.
  </p>
);

export const PlayersFieldset = ({ value, onChange, errorFor }: SampleSectionProps) => {
  const teams = teamOptions(value.teams);
  const update = (index: number, patch: Partial<SamplePlayer>) =>
    onChange({ ...value, players: replaceAt(value.players, index, patch) });

  return (
    <SampleFieldset
      legend="Players"
      hint={<PlayersNotice />}
      addLabel="Add player"
      addTestId="sample-data-add-player"
      onAdd={() => onChange({ ...value, players: [...value.players, emptyPlayer()] })}
    >
      {value.players.map((player, index) => {
        const path = (field: string) => `players[${index}].${field}`;
        const text = (
          field: 'firstName' | 'lastName' | 'phone' | 'email',
          label: string,
          type: 'text' | 'tel' | 'email' = 'text'
        ) => (
          <TextInput
            id={fieldId(path(field))}
            className="sample-form__field"
            label={label}
            type={type}
            value={player[field]}
            error={errorFor(path(field))}
            data-testid={`sample-data-player-${field}-${index}`}
            onChange={(e) => update(index, { [field]: e.target.value } as Partial<SamplePlayer>)}
          />
        );
        return (
          <SampleRow
            key={player.key}
            label={`Player ${index + 1}`}
            removeLabel={`Remove player ${playerName(player, index)}`}
            removeTestId={`sample-data-remove-player-${index}`}
            onRemove={() => onChange(removePlayer(value, player.key))}
          >
            {text('firstName', 'First name')}
            {text('lastName', 'Last name')}
            <KeyedSelect
              label="Position"
              path={path('position')}
              error={errorFor(path('position'))}
              testId={`sample-data-player-position-${index}`}
              options={POSITION_CHOICES}
              value={player.position}
              placeholder="Choose a position"
              onChange={(position) => update(index, { position })}
            />
            <NumberField
              label="Grad year"
              path={path('gradYear')}
              error={errorFor(path('gradYear'))}
              testId={`sample-data-player-gradYear-${index}`}
              value={player.gradYear}
              nullable
              onChange={(gradYear) => update(index, { gradYear })}
            />
            {text('phone', 'Phone', 'tel')}
            {text('email', 'Email', 'email')}
            <KeyedSelect
              label="Team"
              path={path('teamKey')}
              error={errorFor(path('teamKey'))}
              testId={`sample-data-player-team-${index}`}
              options={teams}
              value={player.teamKey}
              placeholder="No team"
              clearable
              onChange={(teamKey) => update(index, { teamKey: teamKey || null })}
            />
          </SampleRow>
        );
      })}
    </SampleFieldset>
  );
};
